'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Search, SlidersHorizontal, Star, Clock, Globe, CheckCircle2, X, Heart, Loader2 } from 'lucide-react';

interface Healer {
  id: string;
  professional_name: string;
  professional_title?: string;
  photo_url?: string;
  rating: number;
  total_reviews: number;
  total_sessions: number;
  experience_years: number;
  minimum_session_amount: number;
  additional_per_minute_rate: number;
  languages: string[];
  specializations: string[];
  is_panel_available: boolean;
  bio?: string;
}

const LANGUAGES = ['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Marathi', 'Gujarati'];
const SORT_OPTIONS = [
  { value: 'rating', label: 'Highest Rated' },
  { value: 'sessions', label: 'Most Sessions' },
  { value: 'price_asc', label: 'Lowest Price' },
  { value: 'price_desc', label: 'Highest Price' },
  { value: 'experience', label: 'Most Experienced' },
];

export default function FindHealersContent() {
  const [healers, setHealers] = useState<Healer[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [language, setLanguage] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const fetchHealers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sortBy,
        page: String(page),
        limit: '12',
      });
      if (search) params.set('search', search);
      if (language) params.set('language', language);
      if (minPrice) params.set('minPrice', minPrice);
      if (maxPrice) params.set('maxPrice', maxPrice);

      const res = await fetch(`/api/healers?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setHealers(data.healers || []);
        setTotal(data.total || 0);
      }
    } catch {}
    setLoading(false);
  }, [search, language, sortBy, minPrice, maxPrice, page]);

  useEffect(() => {
    const timer = setTimeout(fetchHealers, 300);
    return () => clearTimeout(timer);
  }, [fetchHealers]);

  const clearFilters = () => {
    setSearch('');
    setLanguage('');
    setMinPrice('');
    setMaxPrice('');
    setSortBy('rating');
    setPage(1);
  };

  const hasFilters = search || language || minPrice || maxPrice || sortBy !== 'rating';

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-8 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Find Your Healer</h1>
        <p className="text-sm text-muted-foreground">
          {loading ? 'Loading healers...' : `${total} approved healer${total !== 1 ? 's' : ''} available`}
        </p>
      </div>

      {/* Search + Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, specialization, or expertise..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            className="px-4 py-2.5 text-sm bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border transition-all ${showFilters ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-border hover:border-primary/40'}`}
          >
            <SlidersHorizontal size={15} /> Filters
          </button>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-danger border border-danger/30 rounded-xl hover:bg-danger/5 transition-all"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="bg-card border border-border rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4 fade-in">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-2">Language</label>
            <select
              value={language}
              onChange={(e) => { setLanguage(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary"
            >
              <option value="">All Languages</option>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-2">Min Price (₹)</label>
            <input
              type="number"
              value={minPrice}
              onChange={(e) => { setMinPrice(e.target.value); setPage(1); }}
              placeholder="e.g. 500"
              className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-2">Max Price (₹)</label>
            <input
              type="number"
              value={maxPrice}
              onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }}
              placeholder="e.g. 5000"
              className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary"
            />
          </div>
        </div>
      )}

      {/* Healers Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      ) : healers.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          {healers.map((healer) => (
            <div key={healer.id} className="bg-card border border-border rounded-2xl overflow-hidden card-hover group">
              {/* Header */}
              <div className="p-5 pb-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-14 h-14 rounded-2xl gradient-healing flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {healer.professional_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-foreground text-sm leading-tight truncate group-hover:text-primary transition-colors">
                      {healer.professional_name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{healer.professional_title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center gap-1 text-xs font-semibold text-warning">
                        <Star size={11} fill="currentColor" /> {healer.rating || '—'}
                      </div>
                      <span className="text-muted-foreground text-xs">·</span>
                      <span className="text-xs text-muted-foreground">{healer.total_reviews || 0} reviews</span>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {healer.bio && (
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">{healer.bio}</p>
                )}

                {/* Specializations */}
                {healer.specializations && healer.specializations.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {healer.specializations.slice(0, 3).map(s => (
                      <span key={s} className="text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                    {healer.specializations.length > 3 && (
                      <span className="text-[10px] font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full">+{healer.specializations.length - 3}</span>
                    )}
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock size={11} /> {healer.experience_years}y exp</span>
                  <span className="flex items-center gap-1"><CheckCircle2 size={11} /> {healer.total_sessions} sessions</span>
                  {healer.languages && healer.languages.length > 0 && (
                    <span className="flex items-center gap-1"><Globe size={11} /> {healer.languages[0]}</span>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 pb-5 pt-3 border-t border-border flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">From</p>
                  <p className="text-base font-bold text-foreground font-tabular">₹{(healer.minimum_session_amount || 0).toLocaleString('en-IN')}</p>
                  <p className="text-[10px] text-muted-foreground">+₹{healer.additional_per_minute_rate}/min extra</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Link
                    href={`/healer-profile?id=${healer.id}`}
                    className="px-4 py-2 text-xs font-semibold text-primary border border-primary/30 rounded-xl hover:bg-primary/5 transition-all text-center"
                  >
                    View Profile
                  </Link>
                  <Link
                    href={`/booking?healer=${healer.id}`}
                    className="px-4 py-2 text-xs font-semibold text-primary-foreground bg-primary rounded-xl hover:bg-primary/90 transition-all text-center"
                  >
                    Book Now
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-24">
          <Heart size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-bold text-foreground mb-2">No healers found</h3>
          <p className="text-sm text-muted-foreground mb-4">Try adjusting your search or filters</p>
          <button onClick={clearFilters} className="px-6 py-2.5 text-sm font-semibold text-primary-foreground bg-primary rounded-xl hover:bg-primary/90 transition-all">
            Clear Filters
          </button>
        </div>
      )}

      {/* Pagination */}
      {total > 12 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="px-4 py-2 text-sm font-semibold text-foreground border border-border rounded-xl hover:border-primary/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / 12)}</span>
          <button
            disabled={page >= Math.ceil(total / 12)}
            onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 text-sm font-semibold text-foreground border border-border rounded-xl hover:border-primary/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
